import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { debounce } from 'lodash';

export interface MultiSelectOption {
    id: number;
    name: string;
    extra?: string;
}

interface MultiSelectProps {
    options: MultiSelectOption[];
    value: number[];
    onChange: (value: number[]) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    maxDisplayedTags?: number;
    className?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = "Select items...",
    searchPlaceholder = "Search...",
    disabled = false,
    loading = false,
    error,
    maxDisplayedTags = 3,
    className,
}) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const debouncedSetSearchQuery = useMemo(() => debounce(setSearchQuery, 300), []);

    // Filter options based on search
    const filteredOptions = useMemo(() => {
        if (!searchQuery) return options;
        const query = searchQuery.toLowerCase();
        return options.filter(option =>
            option.name.toLowerCase().includes(query) ||
            (option.extra && option.extra.toLowerCase().includes(query))
        );
    }, [options, searchQuery]);

    // Get selected option objects
    const selectedOptions = useMemo(() => {
        return options.filter(option => value.includes(option.id));
    }, [options, value]);

    const toggleOption = (optionId: number) => {
        const newValue = value.includes(optionId)
            ? value.filter(id => id !== optionId)
            : [...value, optionId];
        onChange(newValue);
    };

    const removeOption = (optionId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter(id => id !== optionId));
    };

    const clearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    const selectAll = () => {
        onChange(filteredOptions.map(opt => opt.id));
    };

    const displayValue = () => {
        if (selectedOptions.length === 0) {
            return <span className="text-muted-foreground">{placeholder}</span>;
        }

        if (selectedOptions.length <= maxDisplayedTags) {
            return (
                <div className="flex flex-wrap gap-1">
                    {selectedOptions.map(option => (
                        <Badge
                            key={option.id}
                            variant="secondary"
                            className="px-1.5 py-0 h-5 text-xs"
                        >
                            {option.name}
                            <X
                                className="ml-1 h-3 w-3 cursor-pointer hover:text-destructive"
                                onClick={(e) => removeOption(option.id, e)}
                            />
                        </Badge>
                    ))}
                </div>
            );
        }

        return (
            <div className="flex items-center gap-1">
                <Badge variant="secondary" className="px-1.5 py-0 h-5 text-xs">
                    {selectedOptions.length} selected
                </Badge>
                <X
                    className="h-4 w-4 cursor-pointer hover:text-destructive"
                    onClick={clearAll}
                />
            </div>
        );
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-full justify-between min-h-[2.5rem] h-auto",
                        !value.length && "text-muted-foreground",
                        className
                    )}
                    disabled={disabled}
                >
                    <div className="flex-1 text-left">
                        {displayValue()}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        onValueChange={debouncedSetSearchQuery}
                    />
                    <CommandList>
                        {loading && (
                            <div className="p-2 text-center text-sm text-muted-foreground">
                                Loading...
                            </div>
                        )}
                        {error && (
                            <div className="p-2 text-center text-sm text-destructive">
                                {error}
                            </div>
                        )}
                        {!loading && !error && filteredOptions.length === 0 && (
                            <CommandEmpty>No items found.</CommandEmpty>
                        )}
                        {!loading && !error && filteredOptions.length > 0 && (
                            <>
                                <div className="p-1 border-b">
                                    <div className="flex gap-1">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 text-xs flex-1"
                                            onClick={selectAll}
                                        >
                                            Select All ({filteredOptions.length})
                                        </Button>
                                        {value.length > 0 && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 text-xs flex-1"
                                                onClick={() => onChange([])}
                                            >
                                                Clear All
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <ScrollArea className="max-h-52">
                                    <CommandGroup>
                                        {filteredOptions.map((option) => (
                                            <CommandItem
                                                key={option.id}
                                                value={option.name}
                                                onSelect={() => toggleOption(option.id)}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        value.includes(option.id) ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {option.name}
                                                {option.extra && (
                                                    <span className="ml-2 text-xs text-muted-foreground">
                                                        {option.extra}
                                                    </span>
                                                )}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </ScrollArea>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};